/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { reset } from 'redux-form'
import styled from 'styled-components/macro'
import Overlay from 'components/molecules/overlay'
import { useProgressiveForm } from 'hooks/use-progressive-form'
import ComplexForm from 'components/molecules/complex-form'
import { useHistory } from 'react-router-dom'
import toast from 'utilities/toast'
import Typography from 'components/molecules/typography'
import Icon from 'components/atoms/icon'
import Link from 'components/atoms/link'
import { toKebabCase } from 'utilities/format'
import ExplanationParagraph from 'components/atoms/explanation-paragraph'

const StyledIcon = styled(Icon)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  top: -1px;

  > svg {
    fill: currentColor;
  }
`

const FileList = styled.ul`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizings.lvl2};
  > li {
    display: block;
  }
`

const FormOverlay = ({
  closeOverlay,
  data,
  formCategory,
  formId,
  forms,
  icon,
  onSubmit,
  doNotResetOnClose,
  resetOnsubmit,
  storeFormId,
  submitText,
  text,
  title,
  enableReinitialize,
}) => {
  const reduxDispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const overlayId = 'form'
  const categoryForms = forms[formCategory].data || {}
  const form = formId ? categoryForms[formId] : categoryForms

  const {
    fieldsets: currentFieldsets,
    endpoint: currentEndpoint,
    submitText: currentSubmitText,
    data: currentData,
    generatesDownloadableFiles: currentGeneratesDownloadableFiles,
    reload: currentReload,
    redirect: currentRedirect,
    wrapOnSubmit,
  } = useProgressiveForm({
    initialFieldsets: form?.fieldsets || [],
    initialEndpoint: form?.endpoint,
    initialSubmitText: submitText,
    initialData: data,
    isProgressive: form?.isProgressive || false,
    initialGeneratesDownloadableFiles:
      form?.generatesDownloadableFiles || false,
    initialReload: form?.reload || false,
    initialRedirect: form?.redirect || null,
  })

  const [filesSets, setFilesSets] = useState(null) // initially unset/null
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const timeoutId = useRef(null)
  const mounted = useRef(false)

  // There's an async request and mounting happening in this component,
  // we only want to display components or trigger actions when the component is still mounted
  useEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false

      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
      }
    }
  }, [])

  // Handle post-submit redirect/reload
  useEffect(() => {
    if (submitted && currentReload && !currentGeneratesDownloadableFiles) {
      history.push(currentRedirect)
      window.location.reload()
    }
  }, [
    submitted,
    currentReload,
    currentGeneratesDownloadableFiles,
    currentRedirect,
    history,
  ])

  const handleCloseOverlay = () => {
    if (doNotResetOnClose) {
      reduxDispatch(reset(storeFormId))
    }
    closeOverlay(overlayId)
  }

  const handleSubmit = (values) => {
    setIsSubmitting(true)

    // Wrap onSubmit to handle progressive forms
    const wrappedOnSubmit = wrapOnSubmit(onSubmit)
    const endpoint = currentEndpoint || form.endpoint
    const request = wrappedOnSubmit && wrappedOnSubmit(values, endpoint)

    if (!request) {
      if (mounted.current) {
        setIsSubmitting(false)
      }
      return
    }

    // Handle both sync and async responses
    const handleResponse = (response) => {
      if (!mounted.current) {
        return
      }

      // Check if progressive form should continue
      // The wrapOnSubmit hook already handled the progressive form logic
      if (response?._progressiveContinue) {
        // Progressive form continues - overlay stays open, form updates
        setIsSubmitting(false)
        return
      }

      // Normal form completion flow (no next step)
      setSubmitted(true)

      /**
       * form.generatesDownloadableFiles is part of
       * a form config sent from the backend API.
       * This way the backend can control this user-flow.
       */
      if (response?.success) {
        toast.formNotification(
          response._notification,
          t('formFeedback.successfullySaved'),
        )
      }

      if (currentReload) {
        // some time to read the message:
        timeoutId.current = setTimeout(() => {
          window.location.reload()
        }, 2000)
      }

      // generatesDownloadableFiles can come from original form or nextForm in progressive flows
      if (currentGeneratesDownloadableFiles) {
        setFilesSets(response?.data?.downloadableFileSets || [])
      } else {
        // Close overlay when form completes (unless generating files)
        handleCloseOverlay()
      }

      if (currentRedirect) {
        history.push(currentRedirect)
      }
    }

    // Handle async or sync responses
    if (request && typeof request.then === 'function') {
      request
        .then(handleResponse)
        .catch((error) => {
          if (mounted.current) {
            console.error('Form submission error:', error)
          }
        })
        .finally(() => {
          if (mounted.current) {
            setIsSubmitting(false)
          }
        })
    } else {
      handleResponse(request)
      if (mounted.current) {
        setIsSubmitting(false)
      }
    }

    // Handle non-async operations after request is initiated
    if (resetOnsubmit) {
      reduxDispatch(reset(storeFormId))
    }

    // Note: Overlay closing is handled in the promise chain based on response
    // Only handle redirect here (if needed before async completes)
    if (currentRedirect && !request) {
      history.push(currentRedirect)
    }
  }

  return (
    <Overlay
      close={handleCloseOverlay}
      icon={icon}
      text={text}
      title={title}
      data-test-e2e={`overlay-${toKebabCase(storeFormId)}`}
    >
      {!submitted && (
        <ComplexForm
          data={currentData}
          fieldsets={currentFieldsets}
          formId={storeFormId}
          onSubmit={handleSubmit}
          submitText={currentSubmitText}
          level="cta"
          enableReinitialize={enableReinitialize}
          isLoading={isSubmitting}
        />
      )}

      {Array.isArray(filesSets) && filesSets.length > 0 && (
        <>
          {/* @todo: move this to its own component */}
          {filesSets.map((fileSet, fileSetIndex) => (
            <section key={`fileset-${fileSetIndex}`}>
              {fileSet.description && (
                <Typography type="ExplanationParagraph">
                  {fileSet.description}
                </Typography>
              )}
              {Array.isArray(fileSet.files) && (
                <FileList>
                  {fileSet.files.map((file, fileIndex) => (
                    <li key={`file-${fileIndex}`}>
                      <Link href={file.path}>
                        {/* @todo: generalise this icon */}
                        <StyledIcon type="printPricetag" />
                        &nbsp;
                        {file.name}
                      </Link>
                    </li>
                  ))}
                </FileList>
              )}
            </section>
          ))}
        </>
      )}

      {Array.isArray(filesSets) && filesSets.length === 0 && (
        <ExplanationParagraph>
          {t('formOverlay.noFilesInFileSet')}
        </ExplanationParagraph>
      )}
    </Overlay>
  )
}

FormOverlay.propTypes = {
  closeOverlay: PropTypes.func.isRequired,
  data: PropTypes.object,
  enableReinitialize: PropTypes.bool,
  formCategory: PropTypes.string.isRequired,
  formId: PropTypes.string,
  forms: PropTypes.object.isRequired,
  icon: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  resetOnsubmit: PropTypes.bool,
  submitText: PropTypes.string,
  storeFormId: PropTypes.string.isRequired,
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      type: PropTypes.oneOf([Trans]),
    }),
  ]),
  title: PropTypes.string.isRequired,
  /**
   * forms in overlays should (in general) reset any changes
   * to the initial values when not saved, but there are some exceptions.
   */
  doNotResetOnClose: PropTypes.bool,
}

FormOverlay.defaultProps = {
  data: null,
  enableReinitialize: false,
  forms: null,
  formId: null,
  resetOnsubmit: false,
  text: null,
  doNotResetOnClose: true,
}

export default FormOverlay
